import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { axiosGet } from '../utils/axiosGet';
import { useLoggedInUserContextProvider } from './LoggedInUserContext';
import { useAdminCollections } from './AdminCollections';
import { devError } from '../utils/devLog';

const RefereeCollections = createContext();

export const RefereeCollectionsProvider = ({ children }) => {
  const [candidates, setCandidates] = useState({});
  const [nominationArray, setNominationArray] = useState([]);
  const [refereeReports, setRefereeReports] = useState([]);
  const [nonAdminRefereeReportId, setNonAdminRefereeReportId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { userDetails } = useLoggedInUserContextProvider();
  const {
    nominationArray: adminNominations,
    candidates: adminCandidates,
    isLoading: adminCollectionsLoading,
  } = useAdminCollections();
  const prevUserIdRef = useRef(null);
  const fetchIdRef = useRef(0);
  const location = useLocation();

  const removeRefereeReport = useCallback((reportId) => {
    setRefereeReports((prevReports) =>
      prevReports.filter((report) => report.id !== reportId),
    );
  }, []);

  const updateRefereeReport = useCallback((updatedReport) => {
    setRefereeReports((prevReports) => {
      const index = prevReports.findIndex(
        (report) => report.id === updatedReport.id,
      );

      if (index !== -1) {
        const updatedReports = [...prevReports];
        updatedReports[index] = updatedReport;
        return updatedReports;
      }

      return [...prevReports, updatedReport];
    });
  }, []);

  const refreshRefereeReportsByUserIds = useCallback(async (refereeUserIds) => {
    if (!Array.isArray(refereeUserIds) || refereeUserIds.length === 0) {
      return;
    }

    try {
      const reportRequests = refereeUserIds.map((userId) =>
        axiosGet(
          `referee-reports?filters[refereeUserId][$eq]=${userId}&filters[refereeArchived][$eq]=false&populate=*`,
        ),
      );

      const reportResponses = await Promise.all(reportRequests);

      const newReports = reportResponses
        .flatMap((response) => response?.data?.data || [])
        .filter(Boolean);

      if (newReports.length > 0) {
        setRefereeReports((prevReports) => {
          const existingIds = new Set(prevReports.map((r) => r.id));
          const reportsToAdd = newReports.filter((r) => !existingIds.has(r.id));
          return [...prevReports, ...reportsToAdd];
        });
      }
    } catch (error) {
      devError('Error refreshing referee reports by user IDs:', error);
    }
  }, []);

  useEffect(() => {
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;

    const fetchData = async () => {
      if (!userDetails || adminCollectionsLoading) return;

      setIsLoading(true);

      if (
        prevUserIdRef.current !== null &&
        prevUserIdRef.current !== userDetails.id
      ) {
        setRefereeReports([]);
      }
      prevUserIdRef.current = userDetails.id;

      try {
        const isAdmin =
          userDetails.roles.includes('admin') ||
          userDetails.roles.includes('super-admin');
        const isSCChair = userDetails.roles.includes('committee-chair');
        const isSecretary = userDetails.roles.includes('secretary');
        const isOnRefereePage = location.pathname.includes('referee');

        const loggedInUserId = userDetails.id;

        const shouldFetchAllReports =
          isAdmin || ((isSCChair || isSecretary) && !isOnRefereePage);

        const query = shouldFetchAllReports
          ? `referee-reports?filters[refereeArchived][$eq]=false&populate=*&pagination[limit]=6000`
          : `referee-reports?filters[refereeUserId][$eq]=${loggedInUserId}&filters[refereeArchived][$eq]=false&populate=*`;

        const refereeReportsResponse = await axiosGet(query);

        if (currentFetchId !== fetchIdRef.current) {
          return;
        }

        const reportsData = refereeReportsResponse?.data?.data || [];
        setRefereeReports(reportsData);

        const nominationIds = new Set(
          reportsData.map((report) => report.attributes.nominationId),
        );

        const filteredNominations = adminNominations.filter((nom) =>
          nominationIds.has(nom.id),
        );

        const candidateIds = new Set(
          filteredNominations.map((nom) => nom.attributes.candidateId),
        );

        const filteredCandidates = Object.keys(adminCandidates)
          .filter((id) => candidateIds.has(parseInt(id)))
          .reduce((obj, id) => {
            obj[id] = adminCandidates[id];
            return obj;
          }, {});

        setNominationArray(filteredNominations);
        setCandidates(filteredCandidates);
        setIsLoading(false);
      } catch (error) {
        devError('Error fetching data:', error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    userDetails,
    adminNominations,
    adminCandidates,
    adminCollectionsLoading,
    location.pathname,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      if (userDetails && nonAdminRefereeReportId) {
        try {
          const isAdmin =
            userDetails.roles.includes('admin') ||
            userDetails.roles.includes('super-admin');

          const query = isAdmin
            ? `referee-reports/${nonAdminRefereeReportId}?populate=*`
            : `referee-reports/${nonAdminRefereeReportId}?filters[refereeUserId][$eq]=${userDetails.id}&filters[refereeArchived][$eq]=false&populate=*`;

          const response = await axiosGet(query);

          if (response?.data?.data) {
            updateRefereeReport(response.data.data);
          } else {
            removeRefereeReport(nonAdminRefereeReportId);
          }

          setNonAdminRefereeReportId(null);
        } catch (error) {
          devError('Error fetching single referee report:', error);

          if (error.response?.status === 404) {
            removeRefereeReport(nonAdminRefereeReportId);
            setNonAdminRefereeReportId(null);
          }
        }
      }
    };

    fetchData();
  }, [
    userDetails,
    nonAdminRefereeReportId,
    updateRefereeReport,
    removeRefereeReport,
  ]);

  return (
    <RefereeCollections.Provider
      value={useMemo(
        () => ({
          nonAdminRefereeReportId,
          setNonAdminRefereeReportId,
          candidates,
          nominationArray,
          refereeReports,
          removeRefereeReport,
          updateRefereeReport,
          refreshRefereeReportsByUserIds,
          isLoading,
        }),
        [
          nonAdminRefereeReportId,
          candidates,
          nominationArray,
          refereeReports,
          removeRefereeReport,
          updateRefereeReport,
          refreshRefereeReportsByUserIds,
          isLoading,
        ],
      )}
    >
      {children}
    </RefereeCollections.Provider>
  );
};

RefereeCollectionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useRefereeCollections = () => useContext(RefereeCollections);
